import React from "react";

const DataCitationAndTransparency = () => {
  return (
    <React.Fragment>
      <h1>Data Citation and Transparency</h1>

      <h2>
        <strong>Data Citation</strong>
      </h2>
      <p>
        All data, program code, and other methods should be appropriately cited. Such materials are
        recognized as original intellectual contributions and afforded recognition through citation.
        We recommend citing all data sets and program code used in a publication in the text and
        listing them in the reference section.
      </p>
      <p>
        Authors are encouraged to formally cite any datasets stored in external repositories that
        are mentioned within their manuscript, including the main datasets that are the focus of the
        submission, as well as any other datasets that have been used in the work. For previously
        published datasets, authors should cite both the related research articles and the datasets
        themselves. Appropriate citation of data is checked and enforced by the CJBE Editorial staff
        before publication.
      </p>
      <p>
        References for data sets and program code should include a persistent identifier, such as a
        Digital Object Identifier (DOI). Persistent identifiers ensure future access to unique
        published digital objects, such as a text or data set. Persistent identifiers are assigned
        to data sets by digital archives, such as institutional repositories and partners in the
        Data Preservation Alliance for the Social Sciences (DataPASS).
      </p>

      <h2>
        <strong>Data, Analytic Methods (Code), and Research Materials Transparency</strong>
      </h2>
      <p>
        Authors are encouraged to indicate whether the data, methods used in the analysis, and
        materials used to conduct the research will be made available to any researcher for purposes
        of reproducing the results or replicating the procedure.
      </p>
      <p>
        <strong>
          Authors are encouraged to indicate in the acknowledgments section, if they will or will
          not make their data, analytic methods, and study materials available to other researchers.
          If an author agrees to make materials available, then the author must specify where that
          material will be available.
        </strong>
      </p>

      <h2>
        <strong>Sharing of Raw Data</strong>
      </h2>
      <p>
        We strongly encourage all authors to share their raw data, either by providing it in a
        supplementary file or depositing it in a public repository and providing the details on how
        to access it in their manuscript. If authors do not wish to share their data, then they must
        clearly state this in their manuscript along with a justification. “Availability of Data and
        Materials” statements can take one of the following forms (or a combination of more than one
        if required for multiple datasets):
      </p>
      <ul className="indented-for-medium-up disc">
        <li>
          The datasets generated and/or analyzed during the current study are available in the
          [NAME] repository, [PERSISTENT WEB LINK TO DATASETS].
        </li>
        <li>
          The datasets used and/or analyzed during the current study are available from the
          corresponding author on reasonable request.
        </li>
        <li>
          All data generated or analyzed during this study are included in this published article
          [and its supplementary information files].
        </li>
        <li>
          The datasets generated and/or analyzed during the current study are not publicly available
          due [REASON WHY DATA ARE NOT PUBLIC] but are available from the corresponding author on
          reasonable request.
        </li>
        <li>
          The data that support the findings of this study are available from [third party name] but
          restrictions apply to the availability of these data, which were used under license for
          the current study, and so are not publicly available. Data are however available from the
          authors upon reasonable request and with the permission of [third party name].
        </li>
      </ul>
      <p>
        Please note that if you do wish to share your raw data and do not have consent from all
        patients to publish this data, it will need to be de-identified.
      </p>
      <p>
        If your data falls under the list of data types which must be deposited (listed below), we
        ask that you please provide the relevant accession numbers and the name of the database in
        which your data is stored. If the data has not yet been deposited, please do so and provide
        the relevant information needed to access it in the “Acknowledgments” section in your
        manuscript. Please ensure all data is already released and that any accession numbers/web
        links are in the “Acknowledgments” in their final form, and that all web links and
        accessions allow access to public data. Please see the list of recommended repositories for
        each data type:
      </p>

      <p>
        <a
          href="https://www.springernature.com/gp/authors/research-data-policy/recommended-repositories"
          target="_blank"
          rel="noopener"
        >
          https://www.springernature.com/gp/authors/research-data-policy/recommended-repositories
        </a>
      </p>

      <h2>
        <strong>Design and Analysis Transparency</strong>
      </h2>
      <p>
        Authors should follow standards for disclosing key aspects of the research design and data
        analysis. Authors are encouraged to review the standards available for many research
        applications.
      </p>
    </React.Fragment>
  );
};

export default DataCitationAndTransparency;
